import { useEffect, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { darkTheme } from '../lib/tokens';
import { useAuthStore } from '../features/auth/authStore';
import { logoutFromAdmin } from '../features/auth/adminLogin';
import { signOutFirebase } from '../features/auth/firebaseAuth';
import { NavList, findNavLabel } from './NavList';
import { useNavDrawerStore } from './navDrawerStore';
import { AppPicker } from '../features/app/AppPicker';
import { ROLE_LABEL } from '../features/auth/permissions';

export function AppShell(): JSX.Element {
  const location = useLocation();
  const setDrawerOpen = useNavDrawerStore((s) => s.setOpen);

  // Drawer bei jedem Routenwechsel schliessen — Safety-Net zusaetzlich zum
  // onNavigate der Nav-Links (greift auch bei Back/Forward und programmatischer
  // Navigation, z.B. Redirect nach Logout).
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname, setDrawerOpen]);

  return (
    <div className="flex h-screen bg-bg-base text-text-primary">
      <Sidebar />
      <div className="flex flex-1 flex-col overflow-hidden">
        <MobileTopBar />
        <TopBar />
        <main className="flex-1 overflow-auto p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
      <MobileDrawer />
    </div>
  );
}

// ── Marke (Logo + Wortmarke), geteilt von Sidebar und Drawer ───────────
function BrandHeader(): JSX.Element {
  return (
    <div className="flex items-center gap-3 px-5 py-4">
      <div
        className="flex h-9 w-9 items-center justify-center rounded-lg font-extrabold text-white"
        style={{ background: `linear-gradient(135deg, ${darkTheme.colors.accent} 0%, ${darkTheme.colors.accentLight} 100%)` }}
      >
        C
      </div>
      <div className="flex flex-col leading-tight">
        <span className="text-sm font-bold text-text-primary">Currivium</span>
        <span className="text-[10px] font-semibold uppercase tracking-widest text-accent-light">Admin</span>
      </div>
    </div>
  );
}

// ── Desktop-Sidebar (≥ lg) ─────────────────────────────────────────────
function Sidebar(): JSX.Element {
  return (
    <aside
      className="hidden w-60 shrink-0 flex-col border-r lg:flex"
      style={{ background: darkTheme.colors.bgSurface, borderColor: darkTheme.colors.borderSubtle }}
    >
      <BrandHeader />
      <NavList />
      <SidebarFooter />
    </aside>
  );
}

function SidebarFooter(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();

  async function handleLogout() {
    await logoutFromAdmin();
    await signOutFirebase().catch(() => {});
    navigate('/login', { replace: true });
  }

  const initial = (user?.email ?? '?').charAt(0).toUpperCase();

  return (
    <div
      className="flex items-center gap-3 border-t px-4 py-3"
      style={{ borderColor: darkTheme.colors.borderSubtle }}
    >
      <div
        className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold text-white"
        style={{
          background: `linear-gradient(135deg, ${darkTheme.colors.accent} 0%, ${darkTheme.colors.accentLight} 100%)`,
        }}
      >
        {initial}
      </div>
      <div className="min-w-0 flex-1">
        <div className="truncate text-xs font-semibold text-text-primary">
          {user?.email ?? '—'}
        </div>
        <div className="text-[10px] text-text-tertiary">
          {user?.role ? ROLE_LABEL[user.role] : 'Admin'} · 2FA aktiv
        </div>
      </div>
      <button
        type="button"
        onClick={handleLogout}
        title="Abmelden"
        aria-label="Abmelden"
        className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-md p-1.5 text-text-tertiary hover:bg-bg-elevated hover:text-danger lg:min-h-0 lg:min-w-0"
      >
        ↩
      </button>
    </div>
  );
}

// ── Desktop-TopBar (≥ lg) ──────────────────────────────────────────────
function TopBar(): JSX.Element {
  return (
    <header
      className="hidden h-14 shrink-0 items-center justify-between border-b px-6 lg:flex"
      style={{ background: darkTheme.colors.bgBase, borderColor: darkTheme.colors.borderSubtle }}
    >
      <div className="flex items-center gap-2 text-sm text-text-secondary">
        <span className="text-text-tertiary">admin.currivium.de</span>
        <span className="text-text-tertiary opacity-50">/</span>
        <span className="font-semibold text-text-primary">Übersicht</span>
      </div>
      <div className="flex items-center gap-3">
        <AppPicker />
        <button
          type="button"
          className="rounded-md px-3 py-1.5 text-xs text-text-secondary hover:bg-bg-elevated"
          style={{ background: darkTheme.colors.bgSurface }}
        >
          Suchen <kbd className="ml-2 rounded bg-bg-base px-1.5 py-0.5 text-[10px] text-text-tertiary">⌘K</kbd>
        </button>
      </div>
    </header>
  );
}

// ── Mobile-TopBar (< lg): Hamburger + aktueller Bereich ────────────────
function MobileTopBar(): JSX.Element {
  const toggle = useNavDrawerStore((s) => s.toggle);
  const open = useNavDrawerStore((s) => s.open);
  const location = useLocation();

  return (
    <header
      className="flex min-h-[3.5rem] shrink-0 items-center gap-2 border-b px-2 pt-[env(safe-area-inset-top)] lg:hidden"
      style={{ background: darkTheme.colors.bgBase, borderColor: darkTheme.colors.borderSubtle }}
    >
      <button
        type="button"
        onClick={toggle}
        aria-label="Menü öffnen"
        aria-expanded={open}
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-text-secondary hover:bg-bg-elevated"
      >
        <HamburgerIcon />
      </button>
      <div className="flex min-w-0 flex-col leading-tight">
        <span className="truncate text-sm font-semibold text-text-primary">
          {findNavLabel(location.pathname)}
        </span>
        <span className="text-[10px] text-text-tertiary">admin.currivium.de</span>
      </div>
    </header>
  );
}

function HamburgerIcon(): JSX.Element {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M3 6h18M3 12h18M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

// ── Mobile-Drawer (< lg): Off-Canvas-Navigation ────────────────────────
function MobileDrawer(): JSX.Element {
  const open = useNavDrawerStore((s) => s.open);
  const setOpen = useNavDrawerStore((s) => s.setOpen);
  const panelRef = useRef<HTMLDivElement>(null);

  // Body-Scroll sperren, solange der Drawer offen ist.
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Escape schliesst den Drawer.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, setOpen]);

  // Geschlossenes Panel via `inert` aus Fokus-/A11y-Baum nehmen — es bleibt
  // fuer die Slide-Transition gemountet, soll aber nicht tab-bar sein. Direkt
  // am DOM gesetzt (umgeht fehlende React-18-Typen fuer das inert-Attribut).
  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    if (open) el.removeAttribute('inert');
    else el.setAttribute('inert', '');
  }, [open]);

  return (
    <div className="lg:hidden">
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Menü schließen"
        tabIndex={open ? 0 : -1}
        onClick={() => setOpen(false)}
        className={[
          'fixed inset-0 z-40 bg-black/60 transition-opacity duration-200',
          open ? 'opacity-100' : 'pointer-events-none opacity-0',
        ].join(' ')}
      />
      {/* Panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation"
        className={[
          'fixed inset-y-0 left-0 z-50 flex w-72 max-w-[80vw] flex-col border-r shadow-2xl transition-transform duration-200',
          'pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)]',
          open ? 'translate-x-0' : '-translate-x-full',
        ].join(' ')}
        style={{ background: darkTheme.colors.bgSurface, borderColor: darkTheme.colors.borderSubtle }}
      >
        <BrandHeader />
        <NavList onNavigate={() => setOpen(false)} />
        <SidebarFooter />
      </div>
    </div>
  );
}
